import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import "bootstrap/dist/css/bootstrap.min.css";
import Stripe from "stripe";
// import { loadStripe } from "@stripe/stripe-js";
import "./index.scss";
import { CONFIG } from "../../config/constant";
import { decrypt } from "../../utils";
import logo from "../../assets/images/logo.svg";
import cardsLogo from "../../assets/images/accepted-cards.png";

const CheckoutPage = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, email } = useSelector((state) => state.auth);
  const [searchParams] = useSearchParams();
  const intentData = useRef();
  const card = useRef();
  const stripe = useRef();
  const userData = useRef();
  const cardNumberRef = useRef();
  const cardExpiryRef = useRef();
  const cardCvcRef = useRef();

  const [price, setPrice] = useState(0);
  const [title, setTitle] = useState('');
  const [thumbnail, setThumbnail] = useState('');
  const [formData, setFormData] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [couponData, setCouponData] = useState({ code: '' });
  const [isApplied, setIsApplied] = useState(false);

  useEffect(() => {
    createCardElement();
  }, []);

  useEffect(() => {
    if (email) {
      setFormData({ ...formData, email })
    }
  }, [email]);

  function createCardElement() {
    let pti = searchParams.get("pti");
    let data = atob(pti);
    data = data && JSON.parse(data);
    userData.current = data;
    let amount = userData.current.amount;
    amount = Number(amount || 0).toFixed(2);
    setPrice(amount);
    setTitle(userData.current.title)
    setThumbnail(userData.current.thumbnail);
    stripe.current = window.Stripe(process.env.REACT_APP_STRIPE_PK);
    const style = {
      base: {
        // fontSize: '16px',
        // color: '#32325d',
        // '::placeholder': { color: '#aab7c4' }
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a'
      }
    };

    const elements = stripe.current.elements();
    cardNumberRef.current = elements.create('cardNumber', { disableLink: true, ...style, placeholder: 'Card number', });
    cardExpiryRef.current = elements.create('cardExpiry', { style, placeholder: 'Expiration Date (MM / YY)' });
    cardCvcRef.current = elements.create('cardCvc', { style, placeholder: 'Security code' });
    cardNumberRef.current.mount('#card-number');
    cardExpiryRef.current.mount('#expiry-date');
    cardCvcRef.current.mount('#cvv');
  }

  async function createPaymentIntent() {
    const url = `${CONFIG.BASE_URL}app/create-payment-intent`;
    let resp = await fetch(url, {
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({ amount: price }),
    });
    resp = await resp.json();
    const { clientSecret } = resp;
    if (resp.clientSecret) {
      intentData.current = resp;
      const result = await stripe.current.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardNumberRef.current,
          billing_details: {
            name: email || formData.name,
            phone: formData.phone,
            address: {
              line1: formData.line1,
              city: formData.city,
              postal_code: formData.postal_code,
              state: formData.state,
              country: formData.country,
            },
          },
        },
      });
      const { error: confirmError } = result;
      if (confirmError) {
        userData.current.status = 'failed';
        updateTransactionSuccessStatus(result);
      } else {
        userData.current.status = 'success';
        updateTransactionSuccessStatus(result);
      }
    }
  }

  function setError(error) {
    switch (error.code) {
      case "incomplete_number":
      case "incomplete_expiry":
      case "incomplete_cvc":
        setErrorMessage(error.message);
        break;
    }
  };

  async function generateRandomAlphanumeric(length) {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}


  async function initializeTransaction() {
    const { token, error } = await stripe.current.createToken(cardNumberRef.current);
    if (error) {
      setError(error);
    } else {
      if(price>0){
        createPaymentIntent();
      }else{
        const alphanumericString = await generateRandomAlphanumeric(24);
        userData.current.status = 'success';
        updateTransactionSuccessStatus({paymentIntent:{id:`free-${alphanumericString}`}});
      }
    }
  }

  async function updateTransactionSuccessStatus(result) {
    const url = CONFIG.BASE_URL + CONFIG.PURCHASE_CONFIRM;
    const payload = {
      transactionId: result.paymentIntent.id,
      contentId: userData.current.contentId,
      amount: price,
      currency: 'usd',
      status: userData.current.status,
      coupon: isApplied ? couponData?.code : "",
      platform:'web',
    };
    let resp = await fetch(url, {
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify(payload),
    });
    resp = await resp.json();
    if (resp.status == "success") {
      navigate('/success');
    } else {
      navigate('/fail');
    }
  }

  function cancel() {
    const previousPath = location.state?.from || '/';
    navigate(previousPath);
  }

  async function getCouponDetails() {
    try {
      const stripe = new Stripe(process.env.REACT_APP_STRIPE_SK);
      const coupon = await stripe.coupons.retrieve(couponData.code);
      if (coupon) {
        setCouponData({ ...couponData, message: '', amount_off: coupon.amount_off, percent_off: coupon.percent_off })
        let amount = userData.current?.amount;
        amount = Number(amount || 0);
        if (coupon.amount_off) {
          amount = amount - (coupon.amount_off / 100);
        } else if (coupon.percent_off) {
          amount = amount - (amount * coupon.percent_off / 100);
        }
        amount = Number(amount || 0).toFixed(2);
        setPrice(amount);
        setIsApplied(true);
      }
    } catch (error) {
      if (error.type == 'invalid_request_error' || error.type == 'StripeInvalidRequestError') {
        setCouponData({ ...couponData, message: `invalid coupon code ${couponData.code}` })
      }
    }
  }

  return (
    <div className="checkout-page">
      <div className="checkout-header">
        <img src={logo} />
      </div>
      <div className="checkout-wrapper">
        <div className="left-section">
          <div className="contact-wrapper">
            <label>Contact</label>
            <input type="text" className="user-email" placeholder="Email" value={formData.email} onChange={(e) => { setFormData({ ...formData, email: e.target.value }) }} />
          </div>
          <div className="card-container">
            <label>Card Details</label>
            <p className="instruction">All transactions are secure and encrypted.</p>
            <div className="card-wrapper">
              <div className="card-type" ><span>Credit card</span> <img src={cardsLogo} alt="" /></div>
              <div className="card-number" id="card-number" ></div>
              {/* <input type="text" placeholder="Card number" name="cardNumber" ref={cardRef.current} value={formData.cardNumber} className="card-number" onChange={(e)=>{setFormData({...formData,cardNumber:e.target.value})}} /> */}
              <div className="expiry-wrapper">
                <div className="expiry-date" id="expiry-date" ></div>
                <div className="cvv" id="cvv"></div>
                {/* <input type="text" placeholder="Expiration Date (MM / YY)" value={formData.expiry} className="expiry-date" onChange={(e)=>{setFormData({...formData,expiry:e.target.value})}} />
                <input type="text" placeholder="Security code" value={formData.cvv} className="cvv" onChange={(e)=>{setFormData({...formData,cvv:e.target.value})}} /> */}
              </div>
              <input type="text" placeholder="Name on card" value={formData.name} className="name-on-card" onChange={(e) => { setFormData({ ...formData, name: e.target.value }) }} />
            </div>
            <div className="error-message">{errorMessage}</div>
          </div>

          <div className="address-container">
            <label>Billing Address</label>
            <div className="address-wrapper">
              <div className="address-title">Address</div>
              <input type="text" placeholder="Street" className="card-number" value={formData.line1} onChange={(e) => { setFormData({ ...formData, line1: e.target.value }) }} />
              <div className="city-wrapper">
                <input type="text" placeholder="City" className="city" value={formData.city} onChange={(e) => { setFormData({ ...formData, city: e.target.value }) }} />
                <input type="text" placeholder="State" className="state" value={formData.state} onChange={(e) => { setFormData({ ...formData, state: e.target.value }) }} />
                <input type="text" placeholder="Zip" className="zip" value={formData.postal_code} onChange={(e) => { setFormData({ ...formData, postal_code: e.target.value }) }} />
              </div>
              <input type="text" placeholder="Phone number" className="card-number" value={formData.phone} onChange={(e) => { setFormData({ ...formData, phone: e.target.value }) }} />
            </div>
          </div>
        </div>
        <div className="right-section">
          <div className="content-overview-card">
            <div className="image">
              <img src={thumbnail} />
            </div>
            <div className="desc-wrapper">
              <div className="name">{title}</div>
              <div className="price">${price}</div>
            </div>
            <span className="close-btn" onClick={cancel}></span>
          </div>
          <div className="payment-details-card">
            <div className="title">Payment Details</div>
            <div className="order">
              <span>Order</span>
              <span>${price}</span>
            </div>
            <div className="delivery">
              <span>Delivery</span>
              <span>$0.00</span>
            </div>
            <div className="total">
              <span>Total</span>
              <span>${price}</span>
            </div>
          </div>
          <div className="pay-now" onClick={initializeTransaction}>Pay Now</div>

          <div className="coupon-details">
            {/* <div className="title">Have a coupon?</div> */}
            <div className="coupon-wrapper">
              <label>If you have a coupon code, please apply it below</label>
              <input type="text" className="coupon-code" placeholder="Coupon Code" value={couponData.code} onChange={(e) => { setCouponData({ ...couponData, code: e.target.value, message: "", amount_off: 0, percent_off: 0 }) }} />
              {couponData.message ? <span className="error-message">{couponData.message}</span> : null}
            </div>
            {couponData.code && !isApplied ? <div className="apply" onClick={() => getCouponDetails()}>Apply</div> : <div className="apply disable">{!isApplied ? 'Apply' : 'Applied'}</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
